/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

const LocationDropdown = ({ locations, currentPin, currentMap, onPinSelect, labels = {} }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);

  // Filtern der Standorte basierend auf der aktuellen Karte mit useMemo
  const filteredLocations = useMemo(() => {
    return locations ? Object.fromEntries(Object.entries(locations).filter(([_, location]) => location.map === currentMap)) : {};
  }, [locations, currentMap]);

  // Dropdown-Optionen aus gefilterten Standorten erstellen mit useMemo
  const options = useMemo(() => {
    return Object.entries(filteredLocations).map(([key, location]) => ({
      value: key,
      label: location.headline
    }));
  }, [filteredLocations]);

  // Optionen-Keys zur Verwendung in der Abhängigkeitsarray
  const optionKeys = useMemo(() => {
    return options.map((option) => option.value).join(',');
  }, [options]);

  // Aktualisieren der ausgewählten Option nur wenn nötig
  useEffect(() => {
    // Wenn der aktuelle Pin vorhanden ist und in den gefilterten Standorten existiert
    if (currentPin && filteredLocations[currentPin]) {
      setSelectedOption({
        value: currentPin,
        label: filteredLocations[currentPin].headline
      });
    }
    // Wenn es Optionen gibt, aber der aktuelle Pin nicht in den gefilterten Standorten ist
    else if (options.length > 0 && (!selectedOption || !filteredLocations[selectedOption.value])) {
      setSelectedOption(options[0]);
    }
  }, [currentPin, currentMap, optionKeys]); // Nur auf diese Änderungen reagieren

  // Handler für die Änderung der Auswahl
  const handleSelect = (value) => {
    setIsOpen(false);
    if (onPinSelect && value !== currentPin) {
      onPinSelect(value);
    }
  };

  // Lösung für die dangerouslySetInnerHTML-Warnung - alternativ:
  const renderAddress = (address) => {
    if (!address) return null;

    const parts = address.split('<br>');
    return (
      <div className="location__address">
        {parts.map((part, index) => (
          // Erstelle einen besseren eindeutigen Key
          <p key={`address-part-${part}-${part.length}`}>{part}</p>
        ))}
      </div>
    );
  };

  // Wenn keine Optionen vorhanden sind oder selectedOption nicht gesetzt ist
  if (options.length === 0 || !selectedOption) {
    return null;
  }

  // Aktuelle Standortdetails für die Anzeige
  const currentLocation = selectedOption ? filteredLocations[selectedOption.value] : null;

  return (
    <div className="location__dropdown-container">
      <div className="location__dropdown">
        <div
          className={`location__dropdown-selected ${isOpen ? 'location__dropdown-selected--open' : ''}`}
          role="button"
          tabIndex="0"
          onClick={() => setIsOpen(!isOpen)}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setIsOpen(!isOpen)}
        >
          <span>{selectedOption.label}</span>
          <div className="location__dropdown-arrow">
            <svg viewBox="0 0 24 24" style={{ width: '16px', height: '16px' }}>
              <path d={isOpen ? 'M7 14l5-5 5 5z' : 'M7 10l5 5 5-5z'} />
            </svg>
          </div>
        </div>

        {isOpen && (
          <div className="location__dropdown-options">
            {options.map((option) => (
              <div
                key={option.value}
                className={`location__dropdown-option ${option.value === selectedOption.value ? 'location__dropdown-option--active' : ''}`}
                role="button"
                tabIndex="0"
                onClick={() => handleSelect(option.value)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleSelect(option.value)}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>

      {currentLocation && (
        <div className="location__dropdown-details">
          <div className="location__content-wrapper">
            {/* 1. Standortbild */}
            {currentLocation.image && (
              <div className="location__item-image">
                <img
                  src={currentLocation.image.src}
                  alt={currentLocation.image.alt || ''}
                  width={currentLocation.image.width}
                  height={currentLocation.image.height}
                />
              </div>
            )}

            {/* 2. Info-Container */}
            <div className="location__info-container">
              {/* 3. Adresse */}
              {currentLocation.address && renderAddress(currentLocation.address)}

              {/* 4. Telefon & Fax */}
              {(currentLocation.phone || currentLocation.fax) && (
                <div className="location__contact-info">
                  {currentLocation.phone && (
                    <div className="location__phone">
                      <span className="location__label">{labels.phoneLabel || 'Telefon'}</span>
                      {': '}
                      {currentLocation.phone}
                    </div>
                  )}

                  {currentLocation.fax && (
                    <div className="location__fax">
                      <span className="location__label">{labels.faxLabel || 'Fax'}</span>
                      {': '}
                      {currentLocation.fax}
                    </div>
                  )}
                </div>
              )}

              {/* 5. Route-Button */}
              {currentLocation.routeLink && (
                <div className="location__route-link">
                  <a href={currentLocation.routeLink} className="button button--primary" target="_blank" rel="noopener noreferrer">
                    {labels.routeLabel || 'Route anzeigen'}
                  </a>
                </div>
              )}

              {/* 6. Location Cta */}
              {currentLocation.cta && (
                <div className="location__cta">
                  <a className="button button--primary" href={currentLocation.cta.url}>
                    {currentLocation.cta.label}
                  </a>
                </div>
              )}

              {/* 6. Ansprechpartner-Bereich */}
              {currentLocation.contact && (
                <div className="location__contact-person">
                  <h3 className="location__contact-heading">{labels.contactLabel || 'Ansprechpartner'}</h3>

                  <div className="location__contact-wrapper">
                    {currentLocation.contact.image && (
                      <div className="location__contact-image">
                        <img
                          src={currentLocation.contact.image.src}
                          alt={currentLocation.contact.image.alt || ''}
                          width={currentLocation.contact.image.width}
                          height={currentLocation.contact.image.height}
                        />
                      </div>
                    )}

                    <div className="location__contact-details">
                      <div className="location__contact-name">{currentLocation.contact.name}</div>
                      {currentLocation.contact.position && <div className="location__contact-position">{currentLocation.contact.position}</div>}

                      {currentLocation.contact.email && (
                        <div className="location__contact-email">
                          <a href={`mailto:${currentLocation.contact.email}`} className="button button--secondary">
                            {labels.emailLabel || 'E-Mail schreiben'}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

LocationDropdown.propTypes = {
  locations: PropTypes.shape({}),
  currentPin: PropTypes.string,
  currentMap: PropTypes.string.isRequired,
  onPinSelect: PropTypes.func,
  labels: PropTypes.shape({
    phoneLabel: PropTypes.string,
    faxLabel: PropTypes.string,
    routeLabel: PropTypes.string,
    contactLabel: PropTypes.string,
    emailLabel: PropTypes.string
  })
};

LocationDropdown.defaultProps = {
  locations: {},
  currentPin: null,
  onPinSelect: null,
  labels: {}
};

export default LocationDropdown;
